import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../kit";
import { notice, useDomainState, useUi } from "../../../state/editor/useEditor";
import { TOPBAR_SLOT, topBarButton, useTopBarSlot } from "../TopBar";
import { switchModePatch } from "../basic/mode";
import { useBasicEngine } from "../basic/useBasicEngine";
import { useCustomerLink } from "../configurator/customerLink";
import { OPEN_CUSTOMER_LINK_EVENT, useConfiguratorDraft } from "../configurator/draft";

/**
 * The editor's own top-bar controls, portalled into the bar's slots (see
 * TopBar.tsx for why). Undo/Redo act on every change — Basic, AI Look Match
 * and (since 2026-09-29) every Advanced edit, which records into the same
 * history (basic/applyEngine.ts `recordExternal`). ⌘Z / ⇧⌘Z are wired in
 * inspector/CommandPalette.tsx.
 *
 * "Configurator" shows or hides the customer configurator bar, in Basic and
 * Advanced alike — the one flag Basic › Share's switch and ⌘K also flip, so
 * the bar is one click from anywhere rather than three panels deep.
 */
export function TopBarTools({ modelName }: { modelName?: string }) {
  const [ui, patchUi] = useUi();
  const models = useDomainState("models");
  const engine = useBasicEngine();
  const fileSlot = useTopBarSlot(TOPBAR_SLOT.file);
  const toolsSlot = useTopBarSlot(TOPBAR_SLOT.tools);
  const primarySlot = useTopBarSlot(TOPBAR_SLOT.primary);

  // A glTF's internal scene name ("AuxScene", "Scene") is not the piece's name
  // and tells a jeweller nothing — show a name only when it is one.
  const rawName = modelName ?? models[0]?.name;
  const name = rawName && !/^(aux)?scene\s*\d*$|^untitled|^model$/i.test(rawName.trim()) ? rawName : undefined;
  const basic = ui.inspectorMode === "basic";
  const fail = (e: Error) => notice(`Couldn't do that: ${e.message}`);

  const openExport = () =>
    patchUi(
      basic
        ? { leftPanel: "output", lastBasicLeftPanel: "output" }
        : { rightTab: "output", lastRightTab: "output", lastAdvancedTab: "output" },
    );

  const compareOn = () => void engine.preview().catch(fail);
  const compareOff = () => void engine.endPreview().catch(fail);

  const [config, draft] = useConfiguratorDraft();
  const choices = config.def.groups.reduce((n, g) => n + g.options.length, 0);
  const noModel = models.length === 0;

  // The Vault's top-bar menu asks for the customer link by event (it lives
  // outside the editor). Synchronous dispatch keeps the new tab inside the click.
  const openLink = useCustomerLink().open;
  useEffect(() => {
    window.addEventListener(OPEN_CUSTOMER_LINK_EVENT, openLink);
    return () => window.removeEventListener(OPEN_CUSTOMER_LINK_EVENT, openLink);
  }, [openLink]);

  return (
    <>
      {fileSlot && name
        ? createPortal(
            <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-ed-text-2" data-studio-file>
              <Icon name="description" size={16} className="shrink-0 text-ed-text-3" />
              <span className="truncate">{name}</span>
            </span>,
            fileSlot,
          )
        : null}

      {toolsSlot
        ? createPortal(
            <>
              <button
                className={`${topBarButton} px-3`}
                title={engine.undoLabel() ? `Undo ${engine.undoLabel()} (⌘Z)` : "Undo (⌘Z)"}
                aria-label="Undo"
                disabled={!engine.canUndo() || engine.busy}
                onClick={() => void engine.undo().catch(fail)}
                data-basic-undo
              >
                <Icon name="undo" size={18} />
              </button>
              <button
                className={`${topBarButton} px-3`}
                title={engine.redoLabel() ? `Redo ${engine.redoLabel()} (⇧⌘Z)` : "Redo (⇧⌘Z)"}
                aria-label="Redo"
                disabled={!engine.canRedo() || engine.busy}
                onClick={() => void engine.redo().catch(fail)}
                data-basic-redo
              >
                <Icon name="redo" size={18} />
              </button>
              <button
                className={`${topBarButton} ${engine.previewing ? "bg-ed-active" : ""}`}
                title="Hold to see the piece as it looked when it loaded"
                disabled={!engine.hasChanges() && !engine.previewing}
                onPointerDown={compareOn}
                onPointerUp={compareOff}
                onPointerLeave={() => { if (engine.previewing) compareOff(); }}
                onPointerCancel={compareOff}
                onKeyDown={(e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) compareOn(); }}
                onKeyUp={(e) => { if (e.key === " " || e.key === "Enter") compareOff(); }}
                data-compare
              >
                <Icon name="contrast" size={18} />
                <span className="max-[1100px]:hidden">Compare</span>
              </button>
              <button
                className={`${topBarButton} ${config.on ? "bg-ed-active" : ""}`}
                aria-pressed={config.on}
                aria-label={choices ? `Configurator, ${choices} choice${choices === 1 ? "" : "s"}` : "Configurator"}
                title={
                  noModel
                    ? "Load a piece to set up its customer configurator"
                    : config.on
                      ? "Hide the customer configurator"
                      : "Show the customer configurator: the metals and stones shoppers can switch on your link"
                }
                disabled={noModel}
                onClick={() => draft.setOn(!config.on)}
                data-config-topbar
              >
                <Icon name="storefront" size={18} />
                <span className="max-[1100px]:hidden">Configurator</span>
                {choices > 0 ? (
                  <span
                    className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-ed-ink px-1 text-[11px] font-semibold leading-none text-white"
                    aria-hidden
                    data-config-topbar-count
                  >
                    {choices}
                  </span>
                ) : null}
              </button>
              <div role="radiogroup" aria-label="Mode" className="flex h-[34px] items-center rounded-lg bg-ed-bg p-[3px]" data-mode-switch>
                {(
                  [
                    ["basic", "Basic", "Simple controls in plain words"],
                    ["advanced", "Advanced", "Every setting, slider by slider"],
                  ] as const
                ).map(([mode, word, tip]) => {
                  const on = ui.inspectorMode === mode;
                  return (
                    <button
                      key={mode}
                      role="radio"
                      aria-checked={on}
                      title={tip}
                      onClick={() => { if (!on) patchUi(switchModePatch(ui, mode)); }}
                      className={`h-7 rounded-md px-3.5 text-[13px] font-medium transition-colors ${
                        on ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : "text-ed-text-2 hover:text-ed-text"
                      }`}
                    >
                      {word}
                    </button>
                  );
                })}
              </div>
            </>,
            toolsSlot,
          )
        : null}

      {primarySlot
        ? createPortal(
            <button
              onClick={openExport}
              className="inline-flex h-[34px] items-center gap-1.5 rounded-lg bg-ed-ink px-4 text-[13px] font-semibold text-white transition-colors hover:bg-black"
              data-export-open
            >
              <Icon name="download" size={18} />
              <span className="max-[700px]:hidden">Export</span>
            </button>,
            primarySlot,
          )
        : null}
    </>
  );
}
